import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Grid2 as Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";
import { GridColDef, GridRowParams, GridActionsCellItem, GridDeleteIcon, GridValidRowModel } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { cacheService } from "../../../../Core/Api/services";
import { DbJobRuleInfos } from "../../../../Core/Api/Types/db-job-rule-infos";
import { produce } from "immer";
import { BaseMetadataType } from "../../../../Core/Api/Types/base-metadata-type";
import { DbMetadataMatcherInfos } from "../../../../Core/Api/Types/db-metadata-matcher-infos";
import { CreateNewDbMetadataValue } from "../../../../Core/Utils/asset-object-utils";
import { MetadataCompareOperator } from "../../../../Core/Api/Types/metadata-compare-operator";
import { useTranslation } from "react-i18next";

const COMPARATORS = [
    { name: MetadataCompareOperator.Equals, label: "=" },
    { name: MetadataCompareOperator.NotEquals, label: "=/=" },
    { name: MetadataCompareOperator.GreatherThan, label: ">" },
    { name: MetadataCompareOperator.LowerThan, label: "<" },
    { name: MetadataCompareOperator.Exists, label: "Exists" },
    { name: MetadataCompareOperator.NotExists, label: "Not Exists" },
];

export type MetadataComparatorProps = {
    newJobRule: DbJobRuleInfos;

    setNewJobRule: React.Dispatch<React.SetStateAction<DbJobRuleInfos | null>>;
};

const MetadataComparator: FC<MetadataComparatorProps> = (props) => {
    const { t } = useTranslation();
    const { newJobRule, setNewJobRule } = props;
    const [selectedMeta, setSelectedMeta] = useState<string>("");
    const [selectedComparator, setSelectedComparator] = useState<string>("");
    const [metaCompareValue, setMetaCompareValue] = useState<string | null>(null);

    const metaBaseType = useMemo(() => {
        return cacheService.GetMetadata().find((x) => x.tagName === selectedMeta)?.baseType;
    }, [selectedMeta]);

    const canCompareSelectedMetaToVal = useMemo(() => {
        if (!selectedMeta) return false;
        if (!metaBaseType) return false;

        switch (metaBaseType) {
            case BaseMetadataType.DbDoubleMetadataValueInfos:
            case BaseMetadataType.DbLongMetadataValueInfos:
                return (
                    [
                        MetadataCompareOperator.Equals,
                        MetadataCompareOperator.NotEquals,
                        MetadataCompareOperator.GreatherThan,
                        MetadataCompareOperator.LowerThan,
                    ] as string[]
                ).includes(selectedComparator);

            case BaseMetadataType.DbStringMetadataValueInfos:
                return ([MetadataCompareOperator.Equals, MetadataCompareOperator.NotEquals] as string[]).includes(
                    selectedComparator
                );
            default:
                return false;
        }
    }, [selectedMeta, selectedComparator]);

    const selectedDbMeta = useMemo(() => {
        return cacheService.GetMetadata().find((x) => x.tagName === selectedMeta) || null;
    }, [selectedMeta]);

    const handleAddFieldMatch = () => {
        if (!selectedDbMeta || !selectedDbMeta.baseType || !selectedDbMeta.tagName || !selectedComparator) return;
        setNewJobRule(
            produce((draft) => {
                if (!draft || !draft.filter) return;
                const newMetaMatcher = new DbMetadataMatcherInfos({
                    metadata: CreateNewDbMetadataValue(
                        selectedDbMeta.id,
                        selectedDbMeta.baseType!,
                        selectedDbMeta.tagName!,
                        metaCompareValue
                    ),
                    compareOperator: selectedComparator as MetadataCompareOperator,
                });

                draft.filter.metadataMatch = draft.filter.metadataMatch
                    ? [...draft.filter.metadataMatch, newMetaMatcher]
                    : [newMetaMatcher];
            })
        );

        setMetaCompareValue("");
        setSelectedMeta("");
        setSelectedComparator("");
    };

    const validOperations = useMemo(() => {
        switch (metaBaseType) {
            case BaseMetadataType.DbDoubleMetadataValueInfos:
            case BaseMetadataType.DbLongMetadataValueInfos:
                return COMPARATORS;

            case BaseMetadataType.DbStringMetadataValueInfos:
                return COMPARATORS.filter(
                    (x) => x.name !== MetadataCompareOperator.GreatherThan && x.name !== MetadataCompareOperator.LowerThan
                );
            default:
                return COMPARATORS.filter(
                    (x) => x.name === MetadataCompareOperator.Exists || x.name === MetadataCompareOperator.NotExists
                );
        }
    }, [metaBaseType]);

    useEffect(() => {
        if (!canCompareSelectedMetaToVal) setMetaCompareValue(null);
    }, [canCompareSelectedMetaToVal]);

    useEffect(() => {
        if (!validOperations.map((x) => x.name as string).includes(selectedComparator)) {
            setSelectedComparator("");
            return;
        }

        if (
            selectedComparator === MetadataCompareOperator.Exists ||
            selectedComparator === MetadataCompareOperator.NotExists
        )
            setMetaCompareValue(null);

        if (
            (selectedComparator === MetadataCompareOperator.GreatherThan ||
                selectedComparator === MetadataCompareOperator.LowerThan ||
                selectedComparator === MetadataCompareOperator.NotEquals ||
                selectedComparator === MetadataCompareOperator.Equals) &&
            metaCompareValue === null
        )
            setMetaCompareValue("");
    }, [validOperations, selectedComparator]);

    const metadataMatchCols: GridColDef[] = [
        {
            field: "id",
            headerName: t("table:headerName.fieldName"),
            valueGetter: (params: any, row: any) =>
                cacheService.GetMetadata().find((x) => x.id === row?.id)?.tagName || "UNKNOWN",
            flex: CellSize.small,
            minWidth: CellSize.small,
        },
        {
            field: "fieldType",
            headerName: t("common:type"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (params: GridValidRowModel) => <Chip label={params.value} sx={{ ml: 0.5 }} />,
        },
        {
            field: "compareOperator",
            headerName: t("table:headerName.comparator"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (params) => <Chip label={params.value} sx={{ ml: 0.5 }} />,
        },
        {
            field: "value",
            headerName: t("common:value"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (params) => (params.value === true ? <></> : <Typography variant="body1">{params.value}</Typography>),
        },

        {
            field: "actions",
            headerName: "",
            type: "actions",
            align: "right",
            flex: CellSize.actionsSmall,
            minWidth: CellSize.actionsSmall,
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                let actions: any[] = [];
                actions.push(
                    <GridActionsCellItem
                        icon={<GridDeleteIcon color="error" />}
                        label={t("common:remove")}
                        onClick={() => {
                            setNewJobRule(
                                produce((draft) => {
                                    if (draft?.filter && draft.filter.metadataMatch)
                                        draft.filter.metadataMatch = draft.filter.metadataMatch.filter((x) => x.metadata?.id !== params.id);
                                })
                            );
                        }}
                    />
                );
                return actions;
            },
        },
    ];

    return (
        <Grid maxWidth={"100%"} width={"100%"} size={12}>
            <InputLabel sx={{ mb: 1 }} htmlFor="localized-title">
                {t("field:label.metadataMatcher")}
            </InputLabel>
            {newJobRule.filter?.metadataMatch && newJobRule.filter?.metadataMatch.length > 0 && (
                <Box display={"flex"} justifyContent={"center"} width={"100%"}>
                    <DataTable
                        noControls
                        isRowSelectable={() => false}
                        rows={newJobRule.filter.metadataMatch?.map((x, i) => ({ ...x, ...x.metadata })) || []}
                        columns={metadataMatchCols}
                        rowHeight={32}
                        columnHeaderHeight={32}
                        sx={{ height: "auto", width: "100%" }}
                    />
                </Box>
            )}
            <Grid width={"100%"} container spacing={2}>
                <Grid size={{ sm: 8, md: 4 }}>
                    <Autocomplete
                        fullWidth
                        value={selectedMeta}
                        options={cacheService
                            .GetMetadata()
                            .filter((x) => newJobRule.filter?.metadataMatch?.find((m) => m.metadata?.id === x.id) === undefined)
                            .map((x) => x.tagName)}
                        onChange={(e, v) => {
                            v && setSelectedMeta(v);
                        }}
                        renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.searchIntoMetadata")} />}
                    />
                    {metaBaseType && (
                        <Typography sx={{ m: 1 }} variant="body2" color="info.main">
                            {metaBaseType}
                        </Typography>
                    )}
                </Grid>
                <Grid size={{ sm: 4, md: 2 }}>
                    <Select
                        fullWidth
                        sx={{ mt: 3 }}
                        value={selectedComparator}
                        onChange={(e) => setSelectedComparator(e.target.value)}
                    >
                        {validOperations.map((x) => (
                            <MenuItem key={x.name} value={x.name}>
                                {x.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {selectedDbMeta && !selectedComparator && (
                        <Typography sx={{ m: 1 }} color="error.main" variant="body2">
                            {t("page:body.selectComparator")}
                        </Typography>
                    )}
                </Grid>
                <Grid size={{ sm: 8, md: 4 }}>
                    {canCompareSelectedMetaToVal && (
                        <TextField
                            fullWidth
                            value={metaCompareValue || ""}
                            onChange={(e) => setMetaCompareValue(e.target.value || "")}
                            placeholder={t("field:placeholder.enterValueToCompareMetadata")}
                        />
                    )}

                    {canCompareSelectedMetaToVal && (!metaCompareValue || metaCompareValue === "") && (
                        <Typography sx={{ m: 1 }} color="error.main" variant="body2">
                            {t("page:body.enterValue")}
                        </Typography>
                    )}
                </Grid>
                <Grid size={{ sm: 4, md: 2 }}>
                    <Button
                        onClick={handleAddFieldMatch}
                        disabled={
                            !selectedComparator ||
                            !selectedDbMeta ||
                            (canCompareSelectedMetaToVal && (!metaCompareValue || metaCompareValue === ""))
                        }
                        fullWidth
                        sx={{ mt: 3 }}
                    >
                        {t("button:addFieldMatch")}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default MetadataComparator;
